import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { templateService } from '../services';
import type {
  TemplateResponse,
  TemplateListResponse,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  TemplateQuery,
  TemplateStatus,
  TemplatePreviewResponse,
} from '@troia-v3/types';

/**
 * Hook para listar templates com filtros e paginação
 *
 * @example
 * const { data, isLoading, error } = useTemplates({ page: 1, limit: 10 });
 */
export const useTemplates = (
  query?: TemplateQuery
): UseQueryResult<TemplateListResponse, Error> => {
  return useQuery({
    queryKey: ['templates', query],
    queryFn: () => templateService.getAll(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook para obter um template específico por ID
 *
 * @example
 * const { data: template, isLoading } = useTemplate('template-id-123');
 */
export const useTemplate = (
  id: string | undefined
): UseQueryResult<TemplateResponse, Error> => {
  return useQuery({
    queryKey: ['template', id],
    queryFn: () => templateService.getById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook para criar novo template
 *
 * @example
 * const { mutate: createTemplate, isPending } = useCreateTemplate();
 * createTemplate(templateData, {
 *   onSuccess: (template) => console.log('Template criado:', template),
 *   onError: (error) => console.error('Erro:', error)
 * });
 */
export const useCreateTemplate = (): UseMutationResult<
  TemplateResponse,
  Error,
  CreateTemplateRequest
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTemplateRequest) => templateService.create(data),
    onSuccess: () => {
      // Invalida a lista de templates para refetch
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
};

/**
 * Hook para atualizar template existente
 *
 * @example
 * const { mutate: updateTemplate, isPending } = useUpdateTemplate();
 * updateTemplate({ id: 'template-id', data: updates });
 */
export const useUpdateTemplate = (): UseMutationResult<
  TemplateResponse,
  Error,
  { id: string; data: UpdateTemplateRequest }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTemplateRequest }) =>
      templateService.update(id, data),
    onSuccess: (updatedTemplate) => {
      // Invalida a lista de templates
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      // Atualiza o template específico no cache
      queryClient.invalidateQueries({ queryKey: ['template', updatedTemplate.id] });
    },
  });
};

/**
 * Hook para deletar template
 *
 * @example
 * const { mutate: deleteTemplate, isPending } = useDeleteTemplate();
 * deleteTemplate('template-id-123');
 */
export const useDeleteTemplate = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => templateService.delete(id),
    onSuccess: () => {
      // Invalida a lista de templates para refetch
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
};

/**
 * Hook para atualizar status do template
 *
 * @example
 * const { mutate: updateStatus } = useUpdateTemplateStatus();
 * updateStatus({ id: 'template-id', status: 'APPROVED' });
 */
export const useUpdateTemplateStatus = (): UseMutationResult<
  TemplateResponse,
  Error,
  { id: string; status: TemplateStatus; metadata?: any }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
      metadata,
    }: {
      id: string;
      status: TemplateStatus;
      metadata?: any;
    }) => templateService.updateStatus(id, status, metadata),
    onSuccess: (updatedTemplate) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['template', updatedTemplate.id] });
    },
  });
};

/**
 * Hook para duplicar template
 *
 * @example
 * const { mutate: duplicateTemplate } = useDuplicateTemplate();
 * duplicateTemplate('template-id-123');
 */
export const useDuplicateTemplate = (): UseMutationResult<
  TemplateResponse,
  Error,
  string
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => templateService.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
};

/**
 * Hook para pré-visualizar template com dados de exemplo
 *
 * @example
 * const { mutate: previewTemplate, data: preview } = usePreviewTemplate();
 * previewTemplate({ id: 'template-id', sampleData: { 1: 'John', 2: 'Doe' } });
 */
export const usePreviewTemplate = (): UseMutationResult<
  TemplatePreviewResponse,
  Error,
  { id: string; sampleData: Record<number, string | number | boolean> }
> => {
  return useMutation({
    mutationFn: ({
      id,
      sampleData,
    }: {
      id: string;
      sampleData: Record<number, string | number | boolean>;
    }) => templateService.preview(id, sampleData),
  });
};

/**
 * Hook para buscar templates por status
 *
 * @example
 * const { data: approvedTemplates } = useTemplatesByStatus('APPROVED');
 */
export const useTemplatesByStatus = (
  status: TemplateStatus
): UseQueryResult<TemplateResponse[], Error> => {
  return useQuery({
    queryKey: ['templates', 'status', status],
    queryFn: () => templateService.getByStatus(status),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook para buscar templates por provider
 *
 * @example
 * const { data: whatsappTemplates } = useTemplatesByProvider('provider-id-123');
 */
export const useTemplatesByProvider = (
  providerId: string | undefined
): UseQueryResult<TemplateResponse[], Error> => {
  return useQuery({
    queryKey: ['templates', 'provider', providerId],
    queryFn: () => templateService.getByProvider(providerId!),
    enabled: !!providerId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook para verificar se template pode ser deletado
 *
 * @example
 * const { data: canDelete, isLoading } = useCanDeleteTemplate('template-id-123');
 */
export const useCanDeleteTemplate = (
  id: string | undefined
): UseQueryResult<{ canDelete: boolean; reason?: string }, Error> => {
  return useQuery({
    queryKey: ['template', id, 'can-delete'],
    queryFn: () => templateService.canDelete(id!),
    enabled: !!id,
    staleTime: 0, // Sempre verificar ao montar
  });
};
